'use client';

/**
 * One gate before an edit is applied in the workspace: planEdit over the
 * story (server) → refused (the reason is shown), confirm-required (the
 * EditConfirmDialog with the estimate), or applied right away. A v1 story
 * (no v2 data) applies as before.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { planStoryEdit } from '../actions/staleView';
import { EditConfirmDialog, needsConfirm } from '../edits/components/EditConfirmDialog';
import type { EditKind, EditPlan, EditTarget } from '../edits/types';

export function useEditGate(storyId: string, plan: typeof planStoryEdit = planStoryEdit) {
  const t = useTranslations('stories.edits');
  const [pending, setPending] = useState<{ plan: EditPlan; apply: () => void } | null>(null);

  const guard = useCallback(async (kind: EditKind, target: EditTarget, apply: () => void, onRefused?: (message: string) => void) => {
    const p = await plan(storyId, kind, target).catch(() => null);
    if (!p) { apply(); return; }
    if (p.forbiddenReason) { onRefused?.(t(`forbidden.${p.forbiddenReason}`)); return; }
    if (needsConfirm(p)) { setPending({ plan: p, apply }); return; }
    apply();
  }, [plan, storyId, t]);

  const dialog: ReactNode = pending ? (
    <EditConfirmDialog
      open
      plan={pending.plan}
      onOpenChange={(o) => { if (!o) setPending(null); }}
      onConfirm={() => { pending.apply(); setPending(null); }}
    />
  ) : null;

  return { guard, dialog };
}
